import { useState, useMemo } from 'react';
import { useQuery } from "@tanstack/react-query";
import styled from 'styled-components';

import { ControleOpgelostClient, ControleOpgelost, KleurStatusEnum } from "../client/procescontroleclient";
import Table from "../components/tables/Table";
import Button from "../components/UI/Button";
import Refresh from "../components/UI/Refresh";
import PageTitle from "../components/UI/PageTitle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMediaQuery } from "react-responsive";
import ButtonWrap, { MobileButtonWrap } from '../components/UI/ButtonWrap';
import Input from "../components/UI/Input";
import ProcesControleMobileCard, { Row, Item, Label, Field } from "../components/UI/ProcesControleMobileCard";
import { ButtonType } from "../Types";
import Spinner from "../components/UI/Spinner";
import PageWrapper from '../components/UI/PageWrapper';
import { getDateTimeFormat } from "../utilities/Utilities";
import { InputType, TableType } from '../Types';
import useDebounce from "../hooks/useDebounce";

interface Props {
}

const Tr = styled.tr<Props>`
`;

export default function ProcesControleOpgelost() {
    const client = new ControleOpgelostClient(process.env.REACT_APP_APIURL);
    const [filter, setFilter] = useState<string>('');
    const isMobile = useMediaQuery({ query: '(max-width: 767px)' });
    const debouncedFilter = useDebounce(filter, 300);

    const checkFilterRow: any = (filterRow: ControleOpgelost) => {

        if (debouncedFilter === '') return filterRow;
        const filterableKeys = ['omschrijving', 'debiteurnummer', 'banduitgang', 'orderStatus', 'afdeling', 'hub'];
        return filterableKeys.some(key => (filterRow[key as keyof ControleOpgelost] as string).toLowerCase().includes(debouncedFilter.toLowerCase()));
    }

    const opgelostQuery = useQuery(['controleOpgelost', 'all'], async () => {
        return await client.get();
    },
        {
            staleTime: Infinity
        });

    const filteredOpgelostData = useMemo(() => {

        if (opgelostQuery.data) {
            return opgelostQuery.data.filter(checkFilterRow);
        }
        return [];
    }, [opgelostQuery.data, debouncedFilter]);

    if (opgelostQuery.isLoading) {
        return <Spinner />;
    }

    return (
        <PageWrapper>
            <PageTitle>Opgeloste regels</PageTitle>
            <ButtonWrap>
                {!isMobile ? <Button buttonType={ButtonType.Primary} spacingR svg onClick={() => opgelostQuery.refetch()}><FontAwesomeIcon icon="eye" />Opvragen</Button>
                    : <Refresh onClick={() => opgelostQuery.refetch()} />}
                <MobileButtonWrap>
                    <Input
                        inputType={InputType.SearchInput}
                        onChange={(e: React.FormEvent<HTMLInputElement>) => setFilter(e.currentTarget.value)}
                        value={filter}
                    />
                </MobileButtonWrap>
            </ButtonWrap>
            {!isMobile ?
                <Table tableType={TableType.Default} stickymodal>
                    <thead>
                        <tr>
                            <th>Banduitgang</th>
                            <th>Debnr</th>
                            <th>Hub</th>
                            <th>Afdeling</th>
                            <th>Omschrijving</th>
                            <th style={{ textAlign: 'right' }}>Kolli</th>
                            <th style={{ textAlign: 'right' }}>Inh kolli</th>
                            <th style={{ textAlign: 'right' }}>Los</th>
                            <th style={{ textAlign: 'right' }}>Stuks</th>
                            <th style={{ textAlign: 'right' }}>Lengte</th>
                            <th>Picktijd</th>
                            <th>Inpaktijd</th>
                            <th>Opgelost door</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredOpgelostData?.map((row, index) => (
                            <Tr key={index}>
                                <td>{row.banduitgang}</td>
                                <td>{row.debiteurnummer}</td>
                                <td>{row.hub}</td>
                                <td>{row.afdeling}</td>
                                <td>{row.omschrijving}</td>
                                <td style={{ textAlign: 'right' }}>{row.kolli}</td>
                                <td style={{ textAlign: 'right' }}>{row.kolliInhoud}</td>
                                <td style={{ textAlign: 'right' }}>{row.los}</td>
                                <td style={{ textAlign: 'right' }}>{row.stuks}</td>
                                <td style={{ textAlign: 'right' }}>{row.lengte}</td>
                                <td>{getDateTimeFormat(row.picktijd)}</td>
                                <td>{getDateTimeFormat(row.inpaktijd)}</td>
                                <td>{row.orderStatus}</td>
                            </Tr>
                        ))}
                    </tbody>
                </Table>
                :
                filteredOpgelostData?.map((row, index) => (
                    <ProcesControleMobileCard color={KleurStatusEnum.Empty}
                        compactView={<>
                            <div><strong>Omschr.</strong>{row.omschrijving.substring(0, 15)}{row.omschrijving.length >= 15 && '..'}</div>
                            <div><strong>Debnr.</strong>{row.debiteurnummer}</div>
                            <div><strong>Opgel.</strong>{row.orderStatus}</div>
                        </>}
                        expandView={<>
                            <Row>
                                <Item><Label>Omschrijving</Label><Field>{row.omschrijving}</Field></Item>
                            </Row>
                            <Row>
                                <Item>
                                    <Label>Debnr</Label><Field>{row.debiteurnummer}</Field>
                                </Item>
                                <Item>
                                    <Label>Hub</Label><Field>{row.hub}</Field>
                                </Item>
                            </Row>
                            <Row>
                                <Item>
                                    <Label>Afdeling</Label><Field>{row.afdeling}</Field>
                                </Item>
                                <Item>
                                    <Label>Lengte</Label><Field>{row.lengte}</Field>
                                </Item>
                            </Row>
                            <Row>
                                <Item>
                                    <Label>Kolli</Label><Field>{row.kolli}</Field>
                                </Item>
                                <Item>
                                    <Label>Kolli inh.</Label><Field>{row.kolliInhoud}</Field>
                                </Item>
                                <Item>
                                    <Label>Los</Label><Field>{row.los}</Field>
                                </Item>
                                <Item>
                                    <Label>Stuks</Label><Field>{row.stuks}</Field>
                                </Item>
                            </Row>
                            <Row>
                                <Item>
                                    <Label>Picktijd</Label>
                                    <Field>{getDateTimeFormat(row.picktijd)}</Field>
                                </Item>
                                <Item>
                                    <Label>Inpaktijd</Label>
                                    <Field>{getDateTimeFormat(row.inpaktijd)}</Field>
                                </Item>
                                <Item>
                                    <Label>Opgelost</Label><Field>{row.orderStatus}</Field>
                                </Item>
                            </Row>
                        </>}
                        title={row.banduitgang}
                        checked={undefined} />
                ))}
        </PageWrapper>
    )
}